import { animate, state, style, transition, trigger } from "@angular/animations";
import { isPlatformBrowser } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { ImageAlias } from "@core/enums/image-alias.enum";
import { ImageInterface } from "@core/interfaces/image.interface";
import { DeviceService } from "@core/services/device.service";
import { ImageService } from "@core/services/image/image.service";
import { ImageViewerNavigationContext } from "@core/services/image-viewer.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";

export interface PresentationModeSettings {
  playbackSpeed: number;
  animationSpeed: number;
  animationType: "none" | "fade" | "slide";
  viewMode: "static" | "dynamic";
  autoHideControlsDelay: number;
  blurredBackground: boolean;
}

@Component({
  selector: "astrobin-presentation-mode",
  templateUrl: "./presentation-mode.component.html",
  styleUrls: ["./presentation-mode.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("controlsFade", [
      state("visible", style({ opacity: 1 })),
      state("hidden", style({ opacity: 0 })),
      transition("visible => hidden", animate("500ms ease-out")),
      transition("hidden => visible", animate("200ms ease-in"))
    ])
  ]
})
export class PresentationModeComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  navigationContext: ImageViewerNavigationContext = null;
  @Input() currentIndex: number;
  @Input() settings: PresentationModeSettings;
  @Input() callerComponentId: string;
  @Output() close = new EventEmitter<void>();
  @Output() settingsChanged = new EventEmitter<PresentationModeSettings>();
  @Output() nearEndOfContext = new EventEmitter<string>();
  @ViewChild("imageContainer") imageContainer: ElementRef;
  @ViewChild("imageElement") imageElement: ElementRef<HTMLImageElement>;

  currentImage: ImageInterface;
  nextImageData: ImageInterface;
  isPaused = false;
  showSettings = false;
  controlsVisible = true;
  mobileControlsVisible = true;
  isLoadingImageData = false;
  isLoadingImageFile = false;
  showImageDataLoader = false;
  showImageFileLoader = false;
  isAnimatingProgress = false;
  kenBurnsVariant = 0; // 0-3 for different movement patterns
  imageTransitionKey = 0; // Used to force re-render for animations
  readonly MAX_RETRY_ATTEMPTS = 3;
  private _destroyed$ = new Subject<void>();
  private _hasEmittedNearEndOfContext = false;
  private _playbackSubscription?: Subscription;
  private _mouseActivitySubscription?: Subscription;
  private _imageLoadErrors = new Map<string, number>();
  private _currentImageSubscription?: Subscription;
  private _preloadImageSubscription?: Subscription;
  private _imageDataLoaderSubscription?: Subscription;
  private _imageFileLoaderSubscription?: Subscription;
  private readonly isBrowser: boolean;
  private _isMobile = false;

  constructor(
    private readonly imageService: ImageService,
    private readonly utilsService: UtilsService,
    private readonly deviceService: DeviceService,
    private readonly cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  get animationParams(): any {
    // Note: This getter might be called frequently by Angular change detection
    // Don't put console.log here as it could flood the console
    return {
      animationSpeed: this.settings.animationSpeed,
      duration: this.settings.playbackSpeed * 1000
    };
  }

  get isMobile(): boolean {
    return this._isMobile;
  }

  @HostListener("document:keydown", ["$event"])
  handleKeyboardEvent(event: KeyboardEvent): void {
    switch (event.key) {
      case "Escape":
        this.exitPresentation();
        break;
      case " ":
        event.preventDefault();
        this.togglePlayPause();
        break;
      case "ArrowLeft":
        event.preventDefault();
        this.previousImage();
        break;
      case "ArrowRight":
        event.preventDefault();
        this.nextImage();
        break;
      case "s":
      case "S":
        this.toggleSettings();
        break;
    }
  }

  @HostListener("document:mousemove")
  @HostListener("document:click")
  onUserActivity(): void {
    this.showControls();
    this.resetAutoHideTimer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.navigationContext && this.navigationContext) {
      this.resetNearEndOfContextFlag();
    }
  }

  ngOnInit(): void {
    // Detect if mobile
    this._isMobile = this.deviceService.smMax();

    this.loadCurrentImage();
    this.startPlayback();
    this.resetAutoHideTimer();
    this.enterFullscreen();

    // Listen for fullscreen changes to detect when user exits fullscreen
    if (this.isBrowser) {
      document.addEventListener("fullscreenchange", this.onFullscreenChange);
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();

    if (this._playbackSubscription) {
      this._playbackSubscription.unsubscribe();
    }

    if (this._mouseActivitySubscription) {
      this._mouseActivitySubscription.unsubscribe();
    }

    if (this._imageDataLoaderSubscription) {
      this._imageDataLoaderSubscription.unsubscribe();
    }

    if (this._imageFileLoaderSubscription) {
      this._imageFileLoaderSubscription.unsubscribe();
    }

    if (this._currentImageSubscription) {
      this._currentImageSubscription.unsubscribe();
    }

    if (this._preloadImageSubscription) {
      this._preloadImageSubscription.unsubscribe();
    }

    // Remove fullscreen change listener
    if (this.isBrowser) {
      document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    }

    this.exitFullscreen();
  }

  loadCurrentImage(): void {
    // Cycle through Ken Burns variants for variety
    this.kenBurnsVariant = (this.kenBurnsVariant + 1) % 4;

    // Increment transition key to force animation restart
    this.imageTransitionKey++;

    // Cancel any existing image load
    if (this._currentImageSubscription) {
      this._currentImageSubscription.unsubscribe();
    }

    // Clear any existing loader subscriptions
    if (this._imageDataLoaderSubscription) {
      this._imageDataLoaderSubscription.unsubscribe();
    }
    if (this._imageFileLoaderSubscription) {
      this._imageFileLoaderSubscription.unsubscribe();
    }

    // Reset loader states
    this.isLoadingImageData = false;
    this.isLoadingImageFile = false;
    this.showImageDataLoader = false;
    this.showImageFileLoader = false;

    const contextItem = this.navigationContext[this.currentIndex];

    if (!contextItem) {
      console.error("[PresentationMode] No context item found at index", this.currentIndex);
      return;
    }

    if (contextItem.image) {
      this.currentImage = this.getFinalRevisionForDisplay(contextItem.image);

      // Start image file loading state since we're changing the src
      this.isLoadingImageFile = true;

      // Clear any existing file loader subscription
      if (this._imageFileLoaderSubscription) {
        this._imageFileLoaderSubscription.unsubscribe();
      }

      // Show file loader after delay
      this._imageFileLoaderSubscription = this.utilsService
        .delay(500)
        .pipe(takeUntil(this._destroyed$))
        .subscribe(() => {
          console.log(
            "[PresentationMode] Image file delay elapsed (cached), checking if still loading:",
            this.isLoadingImageFile
          );
          if (this.isLoadingImageFile) {
            this.showImageFileLoader = true;
            this.cdr.markForCheck();
          }
        });

      this.cdr.markForCheck();
      this.preloadNextImage();
      if (this.settings.viewMode === "dynamic") {
        this.startKenBurnsEffect();
      }

      // Schedule next image since we're not waiting for async load
      if (!this.isPaused) {
        this.scheduleNextImage();
      }
    } else {
      // Start loading state
      this.isLoadingImageData = true;
      this.cdr.markForCheck();

      // Show loader after 500ms delay
      this._imageDataLoaderSubscription = this.utilsService
        .delay(500)
        .pipe(takeUntil(this._destroyed$))
        .subscribe(() => {
          if (this.isLoadingImageData) {
            this.showImageDataLoader = true;
            this.cdr.markForCheck();
          }
        });

      // Load the image dynamically
      this._currentImageSubscription = this.imageService
        .loadImage(contextItem.imageId)
        .pipe(takeUntil(this._destroyed$))
        .subscribe({
          next: image => {
            // Clear image data loading state
            this.isLoadingImageData = false;
            this.showImageDataLoader = false;
            if (this._imageDataLoaderSubscription) {
              this._imageDataLoaderSubscription.unsubscribe();
            }

            // Update the navigation context with the loaded image
            this.navigationContext[this.currentIndex].image = image;
            this.currentImage = this.getFinalRevisionForDisplay(image);

            // Start image file loading state since we're changing the src
            this.isLoadingImageFile = true;

            // Clear any existing file loader subscription
            if (this._imageFileLoaderSubscription) {
              this._imageFileLoaderSubscription.unsubscribe();
            }

            // Show file loader after delay
            this._imageFileLoaderSubscription = this.utilsService
              .delay(500)
              .pipe(takeUntil(this._destroyed$))
              .subscribe(() => {
                console.log(
                  "[PresentationMode] Image file delay elapsed (from data load), checking if still loading:",
                  this.isLoadingImageFile
                );
                if (this.isLoadingImageFile) {
                  this.showImageFileLoader = true;
                  this.cdr.markForCheck();
                }
              });

            this.cdr.markForCheck();
            this.preloadNextImage();
            if (this.settings.viewMode === "dynamic") {
              this.startKenBurnsEffect();
            }
          },
          error: error => {
            console.error("[PresentationMode] Failed to load image", contextItem.imageId, error);

            // Clear loading state
            this.isLoadingImageData = false;
            this.showImageDataLoader = false;
            if (this._imageDataLoaderSubscription) {
              this._imageDataLoaderSubscription.unsubscribe();
            }
            this.cdr.markForCheck();

            this.handleImageLoadError();

            // Continue with slideshow even if image failed to load
            if (!this.isPaused) {
              this.scheduleNextImage();
            }
          }
        });
    }
  }

  preloadNextImage(): void {
    const nextIndex = (this.currentIndex + 1) % this.navigationContext.length;
    const nextContextItem = this.navigationContext[nextIndex];

    // Cancel any existing preload
    if (this._preloadImageSubscription) {
      this._preloadImageSubscription.unsubscribe();
    }

    if (nextContextItem?.image) {
      this.nextImageData = this.getFinalRevisionForDisplay(nextContextItem.image);
      this.cdr.markForCheck();
    } else if (nextContextItem) {
      // Preload the next image
      this._preloadImageSubscription = this.imageService
        .loadImage(nextContextItem.imageId)
        .pipe(takeUntil(this._destroyed$))
        .subscribe({
          next: image => {
            this.navigationContext[nextIndex].image = image;
            this.nextImageData = this.getFinalRevisionForDisplay(image);
            this.cdr.markForCheck();
          },
          error: error => {
            console.error("[PresentationMode] Failed to preload next image", nextContextItem.imageId, error);
          }
        });
    }
  }

  startPlayback(): void {
    if (!this.isPaused && this.isBrowser) {
      this.scheduleNextImage();
    }
  }

  stopPlayback(): void {
    if (this._playbackSubscription) {
      this._playbackSubscription.unsubscribe();
      this._playbackSubscription = null;
    }
    this.isAnimatingProgress = false;
    this.cdr.markForCheck();
  }

  togglePlayPause(): void {
    this.isPaused = !this.isPaused;
    if (this.isPaused) {
      this.stopPlayback();
      this.showControls();
    } else {
      this.startPlayback();
      this.resetAutoHideTimer();
    }
    this.cdr.markForCheck();
  }

  nextImage(): void {
    // Wrap to first image when at the last
    this.currentIndex = (this.currentIndex + 1) % this.navigationContext.length;
    this.isAnimatingProgress = false;
    this.cdr.markForCheck();
    this.loadCurrentImage();
    this.checkNearEndOfContext();
  }

  previousImage(): void {
    // Don't wrap - stop at the first image
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.isAnimatingProgress = false;
      this.cdr.markForCheck();
      this.loadCurrentImage();
      this.checkNearEndOfContext();
    }
  }

  handleImageLoadError(): void {
    // Clear image file loading state
    this.isLoadingImageFile = false;
    this.showImageFileLoader = false;
    if (this._imageFileLoaderSubscription) {
      this._imageFileLoaderSubscription.unsubscribe();
    }
    this.cdr.markForCheck();

    const currentItem = this.navigationContext[this.currentIndex];
    if (!currentItem) {
      console.error("[PresentationMode] No current item in handleImageLoadError");
      this.isPaused = true;
      return;
    }

    const imageKey = String(currentItem.imageId);
    const errorCount = this._imageLoadErrors.get(imageKey) || 0;

    if (errorCount < this.MAX_RETRY_ATTEMPTS) {
      this._imageLoadErrors.set(imageKey, errorCount + 1);
      this.utilsService
        .delay(1000)
        .pipe(takeUntil(this._destroyed$))
        .subscribe(() => this.loadCurrentImage());
    } else {
      // Check if all images have errors
      const allImagesHaveErrors = this.navigationContext.every(item => {
        const key = String(item.imageId);
        return (this._imageLoadErrors.get(key) || 0) >= this.MAX_RETRY_ATTEMPTS;
      });

      if (allImagesHaveErrors) {
        console.error("[PresentationMode] All images failed to load, pausing slideshow");
        this.isPaused = true;
        this.cdr.markForCheck();
      } else {
        // Continue with slideshow timing even if current image failed
        if (!this.isPaused) {
          this.scheduleNextImage();
        }
      }
    }
  }

  toggleSettings(): void {
    this.showSettings = !this.showSettings;
    if (this.showSettings) {
      this.pauseForInteraction();
    }
    this.cdr.markForCheck();
  }

  updateSettings(newSettings: PresentationModeSettings): void {
    this.settings = newSettings;
    this.settingsChanged.emit(newSettings);
    this.showSettings = false;

    // Reschedule with new timing if playing
    if (!this.isPaused && this.isBrowser) {
      this.scheduleNextImage();
    }

    this.cdr.markForCheck();
  }

  visitImagePage(): void {
    if (this.currentImage) {
      this.pauseForInteraction();
      window.open(`/i/${this.currentImage.hash || this.currentImage.pk}`, "_blank");
    }
  }

  onImageClick(): void {
    if (this._isMobile) {
      this.nextImage();
    }
  }

  exitPresentation(): void {
    this.close.emit();
  }

  trackByFn(index: number, item: any): any {
    return item;
  }

  onAnimationDone(): void {
    // Animation complete callback if needed
  }

  preloadMoreImages(): void {
    // Placeholder for lazy loading more images
  }

  private getFinalRevisionForDisplay(image: ImageInterface): ImageInterface {
    const finalRevision = this.imageService.getFinalRevision(image);

    // If the final revision is the image itself, return it as-is
    if (finalRevision === image) {
      return image;
    }

    // Otherwise, merge the revision properties with the base image
    // to keep metadata like user, collaborators, equipment, etc.
    return {
      ...image,
      // Override with revision-specific properties
      videoFile: (finalRevision as any).videoFile,
      encodedVideoFile: (finalRevision as any).encodedVideoFile,
      loopVideo: (finalRevision as any).loopVideo,
      thumbnails: (finalRevision as any).thumbnails,
      w: (finalRevision as any).w,
      h: (finalRevision as any).h,
      imageFile: (finalRevision as any).imageFile
    };
  }

  getImageUrl(image: ImageInterface): string {
    if (!image) {
      return "";
    }

    const finalRevision = this.getFinalRevisionForDisplay(image);
    if (!finalRevision || !finalRevision.thumbnails) {
      return "";
    }

    // Try to find QHD first, then HD
    const qhdThumbnail = finalRevision.thumbnails.find(t => t.alias === ImageAlias.QHD);
    if (qhdThumbnail) {
      return qhdThumbnail.url;
    }

    const hdThumbnail = finalRevision.thumbnails.find(t => t.alias === ImageAlias.HD);
    if (hdThumbnail) {
      return hdThumbnail.url;
    }

    // Fallback to regular if neither QHD nor HD available
    const regularThumbnail = finalRevision.thumbnails.find(t => t.alias === ImageAlias.REGULAR);
    if (regularThumbnail) {
      return regularThumbnail.url;
    }

    return "";
  }

  onImageStartLoading(): void {
    this.isLoadingImageFile = true;
    this.cdr.markForCheck();

    // Show loader after 500ms delay
    this._imageFileLoaderSubscription = this.utilsService
      .delay(500)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(() => {
        if (this.isLoadingImageFile) {
          this.showImageFileLoader = true;
          this.cdr.markForCheck();
        }
      });
  }

  onImageLoaded(): void {
    // Clear image file loading state
    this.isLoadingImageFile = false;
    this.showImageFileLoader = false;
    if (this._imageFileLoaderSubscription) {
      this._imageFileLoaderSubscription.unsubscribe();
    }
    this.cdr.markForCheck();

    // Schedule next image if playing
    if (!this.isPaused && this.isBrowser) {
      this.scheduleNextImage();
    }
  }

  private scheduleNextImage(): void {
    if (this._playbackSubscription) {
      this._playbackSubscription.unsubscribe();
    }

    if (!this.isPaused) {
      // Reset animation by toggling the flag
      this.isAnimatingProgress = false;
      this.cdr.markForCheck();

      // Start progress animation after a small delay to ensure the reset is processed
      this.utilsService
        .delay(10)
        .pipe(takeUntil(this._destroyed$))
        .subscribe(() => {
          this.isAnimatingProgress = true;
          this.cdr.markForCheck();
        });

      this._playbackSubscription = this.utilsService
        .delay(this.settings.playbackSpeed * 1000)
        .pipe(takeUntil(this._destroyed$))
        .subscribe(() => {
          if (!this.isPaused) {
            this.nextImage();
          }
        });
    }
  }

  private onFullscreenChange = (): void => {
    // If we're no longer in fullscreen mode, exit presentation mode
    if (!document.fullscreenElement) {
      this.exitPresentation();
    }
  };

  private pauseForInteraction(): void {
    if (!this.isPaused) {
      this.togglePlayPause();
    }
  }

  private showControls(): void {
    this.controlsVisible = true;
    this.cdr.markForCheck();
  }

  private hideControls(): void {
    if (!this.isPaused && !this.showSettings && !this._isMobile) {
      this.controlsVisible = false;
      this.cdr.markForCheck();
    }
  }

  private resetAutoHideTimer(): void {
    if (this._mouseActivitySubscription) {
      this._mouseActivitySubscription.unsubscribe();
    }

    this._mouseActivitySubscription = this.utilsService
      .delay(this.settings.autoHideControlsDelay)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(() => {
        this.hideControls();
      });
  }

  private startKenBurnsEffect(): void {
    // Ken Burns effect removed due to animation issues
    // Can be reimplemented with CSS animations if needed
  }

  private enterFullscreen(): void {
    if (!this.isBrowser) {
      return;
    }

    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen().catch(err => console.error("[PresentationMode] Failed to enter fullscreen:", err));
    } else {
      console.log("[PresentationMode] Fullscreen API not available");
    }
  }

  private exitFullscreen(): void {
    if (!this.isBrowser) {
      return;
    }

    if (document.fullscreenElement) {
      document.exitFullscreen().catch(err => console.error("[PresentationMode] Failed to exit fullscreen:", err));
    }
  }

  private checkNearEndOfContext(): void {
    if (!this.navigationContext || !this.callerComponentId || this._hasEmittedNearEndOfContext) {
      return;
    }

    const totalImages = this.navigationContext.length;
    const nearEndThreshold = 15; // Emit when within 15 images of the end
    const imagesFromEnd = totalImages - this.currentIndex - 1;

    if (imagesFromEnd <= nearEndThreshold) {
      this._hasEmittedNearEndOfContext = true;
      this.nearEndOfContext.emit(this.callerComponentId);
    }
  }

  private resetNearEndOfContextFlag(): void {
    // Reset the flag when more images are loaded (context grows)
    this._hasEmittedNearEndOfContext = false;
  }
}
